import * as React from 'react';
import { cn, getAllViews, isSpecialFolder } from '../../../utils';
import {
  Add,
  Copy,
  SixDots,
  ThreeDots,
  ViewFolder as ViewFolderIcon
} from '../../Icon/icon';
import { Arrow, Edit, Trash } from 'rcd-cpp-ui/ui/icon';
import { Dropdown } from '../../Dropdown/dropdown';
import { View } from '../../../types/View';
import { CSSProperties } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useActions as useNavActions } from '../navActions';
import { NodeType } from '../../../enums/NodeType';
import URLManager from '../../../utils/urlManager';
import { useSelector } from '../../../utils/useSelector';
import { useViewDrop } from './navView';
import { NavViewOptionName } from '../../../enums/NavViewOptionName';
import { ViewItem } from './navViewItem';

type Option = { name: string; Icon: any };
type M = View.Setting | View.Folder;

function getStyle(outline: string): CSSProperties {
  return {
    outline
  };
}

const FolderOptions = [
  { name: NavViewOptionName.AddView, Icon: Add },
  { name: NavViewOptionName.Copy, Icon: Copy },
  { name: NavViewOptionName.Rename, Icon: Edit },
  { name: NavViewOptionName.Delete, Icon: Trash }
];

interface ViewFolderProps {
  isExpanded: boolean;
  handleSelectOption: (o: Option, item?: M, folderRefId?: string) => void;
  disabled: boolean;
  handleSelectView: (v: View.Setting) => void;
  selectedItemId: string;
  f: View.Folder;
  folderName?: string;
  onMoveView?: (viewId: string, toFolderId: string, toIndex: number) => void;
  index?: number;
  onMoveFolder?: (dragIndex: number, hoverIndex: number) => void;
  getWorkspaceItemsOrder?: () => {
    views: View.Setting[];
    folders: View.Folder[];
  };
}
export const ViewFolder = ({
  isExpanded,
  handleSelectOption,
  disabled,
  handleSelectView,
  selectedItemId,
  f,
  folderName,
  onMoveView,
  index,
  onMoveFolder
}: ViewFolderProps) => {
  const greedy = true;
  const selectedViewId = useSelector(s => s.view?.selected?.id);
  const selectedWorkspace = useSelector(s => s.planning?.selectedWorkspace);
  const userSettings = useSelector(s => s.planning?.userSettings);
  const allViews = getAllViews(selectedWorkspace);
  const isSystemFolder = isSpecialFolder(f);
  const isFavoriteFolder = f.props.isFavorite;
  const { onDrop } = useNavActions();
  const favoriteViews = [];
  if (!!allViews && allViews?.length) {
    allViews.forEach(v => {
      const viewSettings = userSettings[v?.props?.refId];
      if (!!viewSettings && viewSettings?.props?.isFavorited) {
        favoriteViews.push(v);
      }
    });
  }

  const { vid } = URLManager.useRouter();
  const [showMenuForId, setShowMenuForId] = React.useState<string>(null);
  const [expandFolder, setExpandFolder] = React.useState(false);
  React.useEffect(() => {
    if (!expandFolder && vid) {
      const v = f?.views?.filter(v => v.props.refId == vid)[0];
      if (v) setExpandFolder(true);
    }
  }, [vid]);
  let folderOptions = isSystemFolder ? null : FolderOptions;
  if (
    f.props?.isBacktagging &&
    selectedWorkspace?.labels?.includes(NodeType.AdminWorkspace)
  ) {
    folderOptions = FolderOptions.filter(
      f => f.name == NavViewOptionName.AddView
    );
  }

  const [{ isOver, isOverCurrent }, viewDrop]: any = useViewDrop({
    isSystemFolder,
    greedy,
    onDropHandler: item => {
      setExpandFolder(true);
      if (onMoveView && item && !isFavoriteFolder) {
        const alreadyInFolder = f.views.some(v => v.props.refId == item.refId);
        if (!alreadyInFolder) {
          onMoveView(item.refId, f.props.refId, f.views.length);
        }
      }
      onDrop();
    },
    onHover: () => setExpandFolder(true)
  });

  let outline =
    isOverCurrent || (isOver && greedy)
      ? `.25px solid #0052d6`
      : '.25px solid transparent';

  const [, drop] = useDrop({
    accept: 'Folder',
    hover(item: { index: number; refId: string }, monitor) {
      if (!item.refId) return;
      const dragRefId = item.refId;
      const hoverRefId = f.props.refId;
      if (dragRefId === hoverRefId) return;
      if (monitor.isOver({ shallow: true })) {
        onMoveFolder?.(item.index, index);
        item.index = index;
        item.refId = hoverRefId;
      }
    },
    drop(item, monitor) {
      if (monitor.isOver({ shallow: true })) {
        onDrop();
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      isOverCurrent: monitor.isOver({ shallow: true })
    })
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'Folder',
    item: { index, refId: f.props.refId },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  return (
    <div
      className="nav-group"
      ref={node => {
        if (!isSystemFolder) {
          drag(drop(node));
          if (viewDrop) viewDrop(node);
        }
      }}
      style={{
        ...getStyle(outline),
        outline: isDragging ? '.25px solid #0052d6' : outline
      }}
    >
      <div
        key={f?.props?.refId}
        className={cn(
          {
            'selected-ngi': f?.id == selectedItemId,
            'show-ngi-menu': f?.id == showMenuForId
          },
          'nav-group-item'
        )}
      >
        {
          <div onClick={() => setExpandFolder(!expandFolder)}>
            <span>
              {!isSystemFolder && <SixDots />}
              <Arrow direction={expandFolder ? 'down' : 'right'} type="solid" />
              <ViewFolderIcon />
            </span>
            <span className="view-name folder-list-item" title={f?.props.name}>
              {folderName ? folderName : f?.props.name}
            </span>
            <span></span>
          </div>
        }
        {!disabled && folderOptions?.length && (
          <div className="nav-group-item-menu">
            <Dropdown
              position="right"
              options={folderOptions}
              renderOption={(o: Option) => {
                const I = o.Icon;
                return (
                  <div>
                    <span>{I && <I />}</span>
                    <span className="dd-text" data-txt={o.name}>
                      {o.name}
                    </span>
                  </div>
                );
              }}
              onSelect={o => handleSelectOption(o, f)}
              onStateChanged={e => {
                setShowMenuForId(e ? f?.id : null);
              }}
            >
              <ThreeDots />
            </Dropdown>
          </div>
        )}
        <div>
          {!!isExpanded && !!expandFolder && isFavoriteFolder && (
            <div>
              {favoriteViews?.map(v => {
                return (
                  <ViewItem
                    disabled={disabled}
                    handleSelectOption={o =>
                      handleSelectOption(o, v, f?.props?.refId)
                    }
                    handleSelectView={handleSelectView}
                    selectedItemId={selectedViewId}
                    v={v}
                    key={v.props.refId}
                    inFavoritedFolder={true}
                  />
                );
              })}
            </div>
          )}
          {!!isExpanded && !!expandFolder && !!f?.views?.length && (
            <div>
              {f?.views?.map((v, i) => {
                return (
                  <ViewItem
                    disabled={disabled}
                    handleSelectOption={o =>
                      handleSelectOption(o, v, f?.props?.refId)
                    }
                    handleSelectView={handleSelectView}
                    selectedItemId={selectedViewId}
                    v={v}
                    key={v.props.refId}
                    index={i}
                    parentRefId={f.props.refId}
                    onMoveView={onMoveView}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
